import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { api, getApiErrorMessage } from '@/lib/api'
import { toast } from 'sonner'
import { PasswordInput } from '@/components/auth/PasswordInput'
import { AuthAlert } from '@/components/auth/AuthAlert'
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton'

export function Login() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totpCode, setTotpCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [touched, setTouched] = useState({ email: false, password: false, totp: false })

  useEffect(() => {
    if (localStorage.getItem('admin_token')) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  const emailError = useMemo(() => {
    if (!email) return 'Email is required'
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address'
    return ''
  }, [email])

  const totpError = useMemo(() => {
    if (!totpCode) return 'TOTP code is required'
    if (!/^\d{6}$/.test(totpCode)) return 'Enter the 6-digit code from your authenticator app'
    return ''
  }, [totpCode])

  const canSubmit = !emailError && password.length > 0 && !totpError

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setTouched({ email: true, password: true, totp: true })
    setError('')

    if (!canSubmit) return

    setLoading(true)
    try {
      const res = await api.post('/admin/login', { email, password, totp_code: totpCode })
      localStorage.setItem('admin_token', res.data.access_token)
      toast.success('Logged in successfully')
      navigate('/')
    } catch (err: any) {
      const message = getApiErrorMessage(err, 'Login failed')
      setError(message)
      toast.error(message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="relative flex min-h-[100dvh] w-full flex-col items-center justify-center p-4 sm:p-6 bg-background">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background"
        aria-hidden="true"
      />

      <div className="relative z-10 w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-3">
          <img src="/logo.svg" alt="PurpleToad Mail" className="h-10 w-10" />
          <span className="text-xl font-bold tracking-tight">PurpleToad Mail</span>
        </div>

        <Card className="w-full border-border/50 shadow-2xl">
          <CardContent className="p-6 sm:p-8">
            <div className="space-y-6">
              <div className="text-center space-y-1.5">
                <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                </div>
                <h1 className="text-2xl font-bold tracking-tight">Admin Login</h1>
                <p className="text-sm text-muted-foreground">
                  Enter your credentials and TOTP code to access the admin console.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && <AuthAlert message={error} />}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    aria-invalid={touched.email && !!emailError}
                    aria-describedby={touched.email && emailError ? 'email-error' : undefined}
                    required
                    autoComplete="email"
                    autoFocus
                  />
                  {touched.email && emailError && (
                    <p id="email-error" className="text-xs text-destructive">{emailError}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <PasswordInput
                    id="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    aria-invalid={touched.password && !password}
                    aria-describedby={touched.password && !password ? 'password-error' : undefined}
                    required
                    autoComplete="current-password"
                  />
                  {touched.password && !password && (
                    <p id="password-error" className="text-xs text-destructive">Password is required</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="totp">TOTP Code</Label>
                  <Input
                    id="totp"
                    type="text"
                    inputMode="numeric"
                    pattern="\d{6}"
                    maxLength={6}
                    placeholder="123456"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onBlur={() => setTouched((t) => ({ ...t, totp: true }))}
                    aria-invalid={touched.totp && !!totpError}
                    aria-describedby={touched.totp && totpError ? 'totp-error' : 'totp-help'}
                    required
                    autoComplete="one-time-code"
                  />
                  {touched.totp && totpError ? (
                    <p id="totp-error" className="text-xs text-destructive">{totpError}</p>
                  ) : (
                    <p id="totp-help" className="text-xs text-muted-foreground">
                      Open your authenticator app and enter the 6-digit code.
                    </p>
                  )}
                </div>

                <AuthSubmitButton loading={loading} disabled={!canSubmit}>
                  Sign In
                </AuthSubmitButton>
              </form>
            </div>
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          &copy; {new Date().getFullYear()} PurpleToad Mail Admin. Authorized access only.
        </p>
      </div>
    </div>
  )
}
